import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminAPI, AdminUser, DailySummary, MealItem } from '../api';

interface UserDetailProps {}

const UserDetail: React.FC<UserDetailProps> = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<AdminUser | null>(null);
  const [dailySummaries, setDailySummaries] = useState<DailySummary[]>([]);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [dayItems, setDayItems] = useState<MealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [dayLoading, setDayLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUserDetails = async () => {
    if (!userId) return;
    
    try {
      setLoading(true);
      const response = await adminAPI.getUserDetails(parseInt(userId));
      
      if (!response.success) {
        setError(response.error || 'Failed to fetch user details');
        return;
      }

      setUser(response.user);
      setDailySummaries(response.dailySummaries);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch user details');
    } finally {
      setLoading(false);
    }
  };

  const fetchDayDetails = async (date: string) => {
    if (!userId) return;
    
    try {
      setDayLoading(true);
      const response = await adminAPI.getUserDayDetails(parseInt(userId), date);
      
      if (!response.success) {
        setError(response.error || 'Failed to fetch day details');
        return;
      }

      setDayItems(response.items);
      setSelectedDay(date);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch day details');
    } finally {
      setDayLoading(false);
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [userId]);

  const handleBackToDashboard = () => {
    navigate('/macrodash');
  };

  const handleDayClick = (date: string) => {
    if (selectedDay === date) {
      setSelectedDay(null);
      setDayItems([]);
    } else {
      fetchDayDetails(date);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <p>Error: {error}</p>
        </div>
        <button
          onClick={handleBackToDashboard}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="container mx-auto p-4">
        <p>User not found</p>
        <button
          onClick={handleBackToDashboard}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          ← Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4">
        <button
          onClick={handleBackToDashboard}
          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
        >
          ← Back to Dashboard
        </button>
      </div>

      <h1 className="text-3xl font-bold mb-6">User Details: {user.email}</h1>
      
      {/* User Info */}
      <div className="bg-white shadow-md rounded-lg mb-6 p-6">
        <h2 className="text-xl font-semibold mb-4">User Information</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600">User ID</p>
            <p className="font-medium">{user.id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Email</p>
            <p className="font-medium">{user.email}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Created</p>
            <p className="font-medium">{formatDate(user.created_at)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600">Last Login</p>
            <p className="font-medium">{user.last_login ? formatDate(user.last_login) : 'Never'}</p>
          </div>
        </div>
      </div>

      {/* Daily Summaries */}
      <div className="bg-white shadow-md rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">Daily Activity Summary</h2>
          <p className="text-sm text-gray-600">Click on a day to see detailed items</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Items
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Calories
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Carbs (g)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Protein (g)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fat (g)
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dailySummaries.map((summary) => (
                <React.Fragment key={summary.date}>
                  <tr 
                    className={`cursor-pointer hover:bg-gray-50 ${
                      selectedDay === summary.date ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleDayClick(summary.date)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {formatDate(summary.date)}
                      {selectedDay === summary.date && (
                        <span className="ml-2 text-blue-600">▼</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {summary.item_count}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round(summary.total_calories)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round(summary.total_carbs)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round(summary.total_protein)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Math.round(summary.total_fat)}
                    </td>
                  </tr>
                  
                  {/* Day Details */}
                  {selectedDay === summary.date && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 bg-gray-50">
                        {dayLoading ? (
                          <div className="flex justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Detailed Items for {formatDate(summary.date)}
                            </h4>
                            {dayItems.length === 0 ? (
                              <p className="text-gray-500">No items found for this day</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                  <thead>
                                    <tr className="border-b border-gray-200">
                                      <th className="text-left py-2">Time</th>
                                      <th className="text-left py-2">Food</th>
                                      <th className="text-left py-2">Qty</th>
                                      <th className="text-left py-2">Calories</th>
                                      <th className="text-left py-2">Carbs</th>
                                      <th className="text-left py-2">Protein</th>
                                      <th className="text-left py-2">Fat</th>
                                      <th className="text-left py-2">Notes</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {dayItems.map((item) => (
                                      <tr key={item.id} className="border-b border-gray-100">
                                        <td className="py-2">{formatDateTime(item.meal_time).split(',')[1]}</td>
                                        <td className="py-2 font-medium">{item.food_name}</td>
                                        <td className="py-2">{item.qty ? `${item.qty} ${item.unit || ''}`.trim() : '-'}</td>
                                        <td className="py-2">{Math.round(item.macros.calories)}</td>
                                        <td className="py-2">{Math.round(item.macros.carbs)}g</td>
                                        <td className="py-2">{Math.round(item.macros.protein)}g</td>
                                        <td className="py-2">{Math.round(item.macros.fat)}g</td>
                                        <td className="py-2 text-gray-600">{item.notes || '-'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        
        {dailySummaries.length === 0 && (
          <div className="px-6 py-8 text-center text-gray-500">
            No activity recorded for this user yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDetail;